import styles from "./SampleEdit.module.css";
import { memo, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, DateTimePicker } from "@wwg/ui";
import { samplePropType } from "shared/propTypes";
import { useMerge } from "shared/hooks/useMerge";
import { TagInput } from "shared/components/TagInput/TagInput";
import { TextArea } from "shared/components/TextArea/TextArea";
import { Heart } from "shared/components/Heart/Heart";

export const SampleEdit = memo(
  ({ sample, disabled, getTagSuggestions, onCancel, onSubmit }) => {
    const { t } = useTranslation();
    const [_sample, _mergeSample] = useMerge(sample);
    const _date = useMemo(
      () =>
        _sample.date instanceof Date ? _sample.date : new Date(_sample.date),
      [_sample]
    );

    return (
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (onSubmit instanceof Function) {
            onSubmit(_sample);
          }
        }}
      >
        <div className={styles.formGroup}>
          <span className={styles.formLabel}>
            {t("labels.sampleEditTagsHeader")}
          </span>
          <TagInput
            disabled={disabled}
            value={_sample.tags}
            placeholder={t("labels.sampleEditTagsInputPlaceholder")}
            getSuggestions={getTagSuggestions}
            isRequired={true}
            onChange={(tags) => {
              _mergeSample({ tags });
            }}
          />
        </div>
        <div className={styles.formGroup}>
          <span className={styles.formLabel}>
            {t("labels.sampleEditDateHeader")}
          </span>
          <DateTimePicker
            disabled={disabled}
            value={_date}
            onChange={(date) => {
              _mergeSample({ date });
            }}
          />
        </div>
        <div className={styles.formGroup}>
          <span className={styles.formLabel}>
            {t("labels.sampleEditNoteHeader")}
          </span>
          <TextArea
            disabled={disabled}
            value={_sample.note}
            onChange={(e) => {
              _mergeSample({ note: e.target.value });
            }}
          />
        </div>
        <div className={styles.checkboxGroup}>
          <Heart
            disabled={disabled}
            id={`sample-${_sample.id}-liked`}
            isChecked={_sample.liked}
            onChange={(liked) => {
              _mergeSample({ liked: !_sample.liked });
            }}
          />
          <label htmlFor={`sample-${_sample.id}-liked`}>
            {t("labels.sampleLiked")}
          </label>
        </div>

        <div className={styles.buttons}>
          <Button
            disabled={disabled}
            buttonStyle="hollow"
            onClick={(e) => {
              e.preventDefault();
              if (onCancel instanceof Function) {
                onCancel();
              }
            }}
          >
            {t("buttons.cancel")}
          </Button>

          <Button type="submit" disabled={disabled || _sample.tags.length === 0}>
            {t("buttons.save")}
          </Button>
        </div>
      </form>
    );
  }
);

SampleEdit.propTypes = {
  sample: samplePropType,
  disabled: PropTypes.bool,

  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,

  /**
   * Async function that takes a search term and returns an array of matching tags.
   */
  getTagSuggestions: PropTypes.func.isRequired,
};
